import type { ReactNode } from 'react'
import { Button } from 'components/Button'
import { RotateCcw } from 'lucide-react'
import { cn } from 'lib/utils'
import { useGenerationProgress } from '../hooks/useVideoProgress'
import { pickLivePhaseLabel } from '../process/phase-steps'
import { useGenerationPhaseStore } from '../store/generation-phase-store'
import type { ContentShape } from '../store/idea-store'
import {
  CAROUSEL_PROGRESS_MESSAGES,
  POST_PROGRESS_MESSAGES,
  VIDEO_PROGRESS_MESSAGES,
} from '../types'

export type GeneratingShape = 'post' | 'carousel' | 'video'

export function toGeneratingShape(shape: ContentShape): GeneratingShape {
  if (shape === 'carousel') return 'carousel'
  if (shape === 'short_vertical_video' || shape === 'long_form_video') return 'video'
  return 'post'
}

export function MediaStatusPill({
  children,
  tone = 'dark',
  className,
}: {
  children: ReactNode
  tone?: 'dark' | 'light'
  className?: string
}) {
  return (
    <span
      className={cn(
        'mono inline-flex min-h-[30px] max-w-[92%] items-center justify-center rounded-[6px] px-3 py-1.5 text-center text-[11px] leading-snug font-medium',
        // Solid plates — translucent pills wash out on bright poster photos.
        tone === 'dark'
          ? 'border border-line bg-bg-2 text-paper shadow-[0_4px_12px_rgba(0,0,0,0.28)]'
          : 'border border-white/15 bg-black/55 text-paper shadow-[0_4px_12px_rgba(0,0,0,0.22)] backdrop-blur-[2px]',
        className
      )}
    >
      {children}
    </span>
  )
}

/** Centers a status pill on a media box — same anchor for loading + error copy. */
export function MediaStatusCenter({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 z-30 flex items-center justify-center px-4',
        className
      )}
    >
      {children}
    </div>
  )
}

export function MediaStatusRetryButton({ onRetry }: { onRetry: () => void }) {
  // pointer-events-auto: rendered inside the pointer-events-none MediaStatusCenter.
  return (
    <div className="pointer-events-auto z-40">
      <Button
        type="button"
        variant="t3-tertiary"
        size="t3-sm"
        onClick={e => {
          e.stopPropagation()
          onRetry()
        }}
      >
        <RotateCcw size={14} />
        Retry
      </Button>
    </div>
  )
}

export function FailedMediaStatusOverlay({
  message,
  className,
  posterUrl,
  onRetry,
  tone = 'dark',
}: {
  message: string
  className: string
  posterUrl?: string
  onRetry?: () => void
  tone?: 'dark' | 'light'
}) {
  // Plate + Retry flow as one centered column: the old fixed 19px-below-center
  // Retry assumed a one/two-line plate and overlapped it on narrow tiles
  // (calendar week cards wrap the message to 4-5 lines).
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {posterUrl && (
        <img
          src={posterUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
      )}
      <MediaStatusCenter>
        <div className="flex flex-col items-center gap-1.5">
          <MediaStatusPill tone={tone}>{message}</MediaStatusPill>
          {onRetry && <MediaStatusRetryButton onRetry={onRetry} />}
        </div>
      </MediaStatusCenter>
    </div>
  )
}

interface GeneratingStatusTextProps {
  shape: GeneratingShape
  isActive: boolean
  /**
   * Pill chrome tone:
   * - `dark` (default): solid T3 plate for Home / Posts cards
   * - `light`: softer dark plate for white preview chrome over photos
   */
  tone?: 'dark' | 'light'
  /**
   * When set, the pill shows the live SocialKit pipeline phase for this creative
   * ("Designing the layout", "Rendering the reel") instead of the generic rotating
   * copy — so first-creation cards narrate real progress. Falls back to the rotation
   * when no beats have streamed yet (or on a non-SocialKit generation).
   */
  creativeId?: string
}

export function GeneratingStatusText({
  shape,
  isActive,
  tone = 'dark',
  creativeId,
}: GeneratingStatusTextProps) {
  // The live phase wins when SocialKit is streaming beats for this creative: show the
  // latest active step's label (or the last step once the run is wrapping up).
  const livePhase = useGenerationPhaseStore(s =>
    creativeId ? pickLivePhaseLabel(s.byCreative[creativeId]?.steps) : undefined
  )
  const messages =
    shape === 'video'
      ? VIDEO_PROGRESS_MESSAGES
      : shape === 'carousel'
        ? CAROUSEL_PROGRESS_MESSAGES
        : POST_PROGRESS_MESSAGES
  const rotating = useGenerationProgress(messages, isActive)
  const text = livePhase ?? rotating
  if (!isActive || !text) return null
  return (
    <MediaStatusCenter>
      <MediaStatusPill tone={tone}>
        {/* Same shimmer as the hero's "Picking the shots…" — work in
            progress reads as alive, not stuck. */}
        <span className="shimmer-text">{text}</span>
      </MediaStatusPill>
    </MediaStatusCenter>
  )
}
